import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { AppState } from '@chegg-wt/core/state/types'
import { AppRoute, AppRouteOpts } from '@chegg-wt/core/types/core'
import React from 'react'
import { connect, useSelector } from 'react-redux'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import { UserRoles } from '@chegg-wt/core/constants/user'
import {
  CheckoutCallbackStates,
  ErrorEnum,
} from '@chegg-wt/core/components/WtCheckout/types'
import OneAuth from '@chegg-wt/core/components/Auth'
import ClientOnlyPortal from '@chegg-wt/core/components/ClientOnlyPortal'
import { EASYBIB } from '@chegg-wt/core/constants/site'
import {
  AuthTokens,
  CheckoutWrapper as WtCheckout,
} from '@chegg-wt/core/components/WtCheckout'
import { getCheckoutCopy } from '@chegg-wt/core/components/WtCheckout/utils'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import {
  afterUpgradingRoute,
  getSessionFormData,
  onClickNoThanks,
} from '@chegg-wt/core/utils/upgrade'
import usePriceTest from '@chegg-wt/core/hooks/usePriceTest'
import useSBMCheck from '@chegg-wt/core/hooks/useSBMCheck'
import useUpgradeUserRedirect from '@chegg-wt/core/hooks/useUpgradeUserRedirect'
import AuthLayout from '@chegg-wt/core/components/SuperAuth/layout'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { logout, refreshAndFetch, upgrade } from '@chegg-wt/core/state/user/actions'
import { currentProjectIdSelector } from '@chegg-wt/core/state/currentProject/selectors'
import {
  idTokenSelector,
  accessTokenSelector,
  userRoleSelector,
} from '@chegg-wt/core/state/user/selectors'
import useNoSoftLoggedIn from '@chegg-wt/core/hooks/useNoSoftLoggedIn'
import { backgroundThroughGamSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { UpgradeQueryParams } from '@chegg-wt/core/types/upgrade'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { useRouter } from 'next/router'
import { ebFeatureList } from '../copy/upgrade'
import { DEFAULT_PRICING_EXPERIMENT, getProductConfig } from '../config/pricingPlans'

const wtCheckoutCallback = (
  refreshAndFetch,
  setHasJustUpgraded,
  upgrade,
  callbackState: CheckoutCallbackStates,
  details: ErrorEnum | Error
) => {
  if (callbackState === CheckoutCallbackStates.PAYMENTDONE) {
    setHasJustUpgraded(true)
    upgrade()
  }
  if (callbackState === CheckoutCallbackStates.ERROR) {
    if (details === ErrorEnum.AuthError) {
      refreshAndFetch()
    }
  }
}

interface UpgradeProps {
  logout: () => void
  navigated: (route: AppRoute, opts?: AppRouteOpts) => void
  upgrade: () => void
  refreshAndFetch: () => void
  authTokens: AuthTokens
  currentProjectId: string
  backgroundThroughGam: boolean
}

const Upgrade: React.FC<UpgradeProps> = ({
  authTokens,
  refreshAndFetch,
  navigated,
  upgrade,
  currentProjectId,
  backgroundThroughGam,
}) => {
  const userRole = useSelector(userRoleSelector)
  useNoSoftLoggedIn()
  useSBMCheck()
  const routes = useRoutesContext()

  const router = useRouter()
  const {
    countryCode: countryCodeParam,
    region: regionCodeParam,
  } = router.query as UpgradeQueryParams

  const { setHasJustUpgraded, shouldShowUpgradeModal } = useUpgradeUserRedirect({
    siteCode: 'EB',
    alreadyUpgradedRoute: afterUpgradingRoute(routes),
  })

  const {
    freeTrial,
    price,
    pricingExperimentReady,
    experiment,
    basePrice,
  } = usePriceTest()

  const productConfig = {
    ...getProductConfig(freeTrial),
    experiment: experiment ?? DEFAULT_PRICING_EXPERIMENT,
    basePrice,
  }
  const pricingCopy = getCheckoutCopy(freeTrial, price, EASYBIB)

  const handleComplete = () => {
    const sessionFormData = getSessionFormData()
    if (sessionFormData?.route?.params && !sessionFormData.route.params.projectId) {
      sessionFormData.route.params.projectId = currentProjectId
    }

    navigated(afterUpgradingRoute(routes, sessionFormData, true))
  }

  const checkoutElement = (
    <ClientOnlyPortal>
      {!pricingExperimentReady && <LoadingDots message="Preparing the upgrade form" />}
      {pricingExperimentReady && (
        <WtCheckout
          authTokens={authTokens}
          site={EASYBIB}
          featureList={ebFeatureList}
          productConfig={productConfig}
          pricingCopy={pricingCopy}
          onHostRedirect={handleComplete}
          checkoutCallback={(callbackState, details) => {
            wtCheckoutCallback(
              refreshAndFetch,
              setHasJustUpgraded,
              upgrade,
              callbackState,
              details
            )
          }}
          countryCode={countryCodeParam}
          region={regionCodeParam}
          onClickNoThanks={onClickNoThanks}
          onClickDismissModal={onClickNoThanks}
        />
      )}
    </ClientOnlyPortal>
  )
  useOnUnloadTimeOnPage()

  return (
    <>
      <SEOTitle title="Upgrade | EasyBib" />
      {userRole === UserRoles.NOT_LOGGED_IN && (
        <OneAuth
          data-test="OneAuth"
          defaultflow="signup"
          backgroundThroughGam={backgroundThroughGam}
        />
      )}
      {shouldShowUpgradeModal && <AuthLayout>{checkoutElement}</AuthLayout>}
    </>
  )
}
const mapStateToProps = (state: AppState) => ({
  authTokens: {
    idToken: idTokenSelector(state),
    accessToken: accessTokenSelector(state),
  },
  currentProjectId: currentProjectIdSelector(state),
  backgroundThroughGam: backgroundThroughGamSelector(state),
})

const mapDispatchToProps = {
  refreshAndFetch,
  logout,
  navigated,
  upgrade,
}

export default connect(mapStateToProps, mapDispatchToProps)(Upgrade)
